import { Component, OnInit, Output, EventEmitter, HostListener, OnDestroy } from '@angular/core';
import { AuthService, UserInfo } from '../../services/auth.service';
import { AuthorizationService } from '../../services/authorization.service';
import { Router } from '@angular/router';
import { ToastService } from '../../services/toast.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Output() sidebarToggled = new EventEmitter<boolean>();

  sidebarCollapsed = false;
  sidebarOpen = true;
  subMenuOpen: any = {
    students: false,
    projects: false,
    enrollments: false,
    incomes: false,
    expenses: false,
    thirdParties: false,
    treasury: false,
    reports: false,
    config: false,
    integrations: false,
    withdrawals: false,
  };

  // Responsive breakpoint
  isMobile = false;

  // User information
  currentUser: UserInfo | null = null;
  private userSubscription: Subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private authorizationService: AuthorizationService,
    private router: Router,
    private toastService: ToastService
  ) {
    this.checkScreenSize();
  }

  ngOnInit(): void {
    this.checkScreenSize();
    this.loadUserInfo();
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
  }

  private loadUserInfo(): void {
    // Suscribirse a cambios en el usuario actual
    this.userSubscription.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUser = user;

        // Si no hay usuario pero está autenticado, intentar obtenerlo
        if (!user && this.authService.isAuthenticated()) {
          this.authService.fetchCurrentUser().subscribe({
            next: (fetchedUser) => {
              this.currentUser = fetchedUser;
            },
            error: (error) => {
              console.warn('No se pudo cargar información del usuario en sidebar:', error);
            }
          });
        }
      })
    );
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth < 1024; // lg breakpoint
    if (this.isMobile) {
      this.sidebarOpen = false;
      this.sidebarCollapsed = false;
    }
  }

  toggleSidebar() {
    if (this.isMobile) {
      this.sidebarOpen = !this.sidebarOpen;
    } else {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    }
    this.sidebarToggled.emit(this.sidebarCollapsed);
  }

  closeSidebar() {
    if (this.isMobile) {
      this.sidebarOpen = false;
    }
  }

  toggleSubMenu(menu: string) {
    // En modo colapsado no abrir submenús
    if (this.sidebarCollapsed && !this.isMobile) {
      this.sidebarCollapsed = false;
      this.sidebarToggled.emit(false);
    }

    // Cerrar otros submenús
    Object.keys(this.subMenuOpen).forEach(key => {
      if (key !== menu) {
        this.subMenuOpen[key] = false;
      }
    });

    this.subMenuOpen[menu] = !this.subMenuOpen[menu];
  }

  // Getter para determinar el estado del sidebar
  get sidebarState() {
    if (this.isMobile) {
      return this.sidebarOpen ? 'mobile-open' : 'mobile-closed';
    }
    return this.sidebarCollapsed ? 'desktop-collapsed' : 'desktop-expanded';
  }

  // Getter para obtener las iniciales del usuario
  get userInitials(): string {
    if (!this.currentUser) {
      return 'U';
    }
    const first = this.currentUser.firstName?.charAt(0) || '';
    const last = this.currentUser.lastName?.charAt(0) || '';
    return (first + last) || this.currentUser.email?.charAt(0)?.toUpperCase() || 'U';
  }

  // Getter para obtener el rol principal del usuario
  get userRole(): string {
    if (!this.currentUser || !this.currentUser.roles || this.currentUser.roles.length === 0) {
      return 'Usuario';
    }

    // Priorizar roles específicos
    if (this.currentUser.roles.includes('SuperAdmin')) return 'SuperAdmin';
    if (this.currentUser.roles.includes('Admin')) return 'Administrador';
    if (this.currentUser.roles.includes('Manager')) return 'Gerente';
    if (this.currentUser.roles.includes('User')) return 'Usuario';

    return this.currentUser.roles[0]; // Devolver el primer rol si no coincide con los anteriores
  }

  // Getter para verificar si el usuario es SuperAdmin
  get isSuperAdmin(): boolean {
    // Primero verificar en el usuario actual
    if (this.currentUser?.roles?.includes('SuperAdmin')) {
      return true;
    }
    // También verificar en el AuthorizationService (por si se cargó del token)
    return this.authorizationService.isSuperAdmin();
  }

  // ============== MÉTODOS DE PERMISOS ==============

  /**
   * Verifica si el usuario tiene un permiso específico
   */
  hasPermission(permission: string): boolean {
    // SuperAdmin tiene todos los permisos
    if (this.isSuperAdmin) return true;
    return this.authorizationService.hasPermission(permission);
  }

  /**
   * Verifica si el usuario tiene alguno de los permisos especificados
   */
  hasAnyPermission(permissions: string[]): boolean {
    // SuperAdmin tiene todos los permisos
    if (this.isSuperAdmin) return true;
    return this.authorizationService.hasAnyPermission(permissions);
  }

  // Getters para cada módulo
  get canViewDashboard(): boolean {
    // Dashboard es accesible para todos los usuarios autenticados
    return true;
  }

  get canViewProjects(): boolean {
    return this.hasPermission('PROJECTS_READ');
  }

  get canViewParticipants(): boolean {
    return this.hasPermission('PARTICIPANTS_READ');
  }

  get canViewSuppliers(): boolean {
    return this.hasPermission('SUPPLIERS_READ');
  }

  get canViewCompanies(): boolean {
    return this.hasPermission('COMPANIES_READ');
  }

  get canViewEnrollments(): boolean {
    return this.hasPermission('ENROLLMENTS_READ');
  }

  get canViewIncome(): boolean {
    return this.hasPermission('INCOME_READ');
  }

  get canViewWorkOrders(): boolean {
    return this.hasPermission('WORKORDERS_READ');
  }

  get canViewExpense(): boolean {
    return this.hasPermission('EXPENSE_READ');
  }

  get canViewRefunds(): boolean {
    return this.hasPermission('REFUNDS_READ');
  }

  get canViewExcess(): boolean {
    return this.hasPermission('EXCESS_READ');
  }

  get canViewReconciliation(): boolean {
    return this.hasPermission('RECONCILIATION_READ');
  }

  get canViewReports(): boolean {
    return this.hasAnyPermission(['REPORTS_BASIC', 'REPORTS_FINANCIAL', 'REPORTS_EXECUTIVE', 'REPORTS_PORTFOLIO', 'REPORTS_PUNISHED_PORTFOLIO']);
  }

  // Permisos para gestión de cartera (retiros, reversiones, aplazamientos)
  get canWithdrawEnrollments(): boolean {
    return this.hasPermission('ENROLLMENTS_WITHDRAW');
  }

  get canReverseWithdrawals(): boolean {
    return this.hasPermission('ENROLLMENTS_REVERSAL');
  }

  get canDeferEnrollments(): boolean {
    return this.hasPermission('ENROLLMENTS_DEFER');
  }

  get canViewPunishedPortfolioReport(): boolean {
    return this.hasPermission('REPORTS_PUNISHED_PORTFOLIO');
  }

  get canViewPortfolioManagement(): boolean {
    return this.canWithdrawEnrollments || this.canReverseWithdrawals || this.canDeferEnrollments;
  }

  // Permisos para gestión de cartera CORPORATIVA
  get canWithdrawCorporateEnrollments(): boolean {
    return this.hasPermission('CORPORATE_ENROLLMENTS_WITHDRAW');
  }

  get canReverseCorporateWithdrawals(): boolean {
    return this.hasPermission('CORPORATE_ENROLLMENTS_REVERSAL');
  }

  get canDeferCorporateEnrollments(): boolean {
    return this.hasPermission('CORPORATE_ENROLLMENTS_DEFER');
  }

  get canViewCorporatePortfolioManagement(): boolean {
    return this.canWithdrawCorporateEnrollments || this.canReverseCorporateWithdrawals || this.canDeferCorporateEnrollments;
  }

  // Permisos para cesiones de cupo
  get canViewEnrollmentTransfers(): boolean {
    return this.hasPermission('ENROLLMENT_TRANSFERS_READ');
  }

  get canCreateEnrollmentTransfers(): boolean {
    return this.hasPermission('ENROLLMENT_TRANSFERS_CREATE');
  }

  get canViewConfig(): boolean {
    return this.hasAnyPermission([
      'CONFIG_BUDGETS_READ', 'CONFIG_BANKS_READ', 'CONFIG_PAYMENT_METHODS_READ',
      'CONFIG_EXCHANGE_READ', 'CONFIG_FEE_TYPES_READ', 'CONFIG_AUDIT_READ',
      'CONFIG_EMAILLOG_READ', 'CONFIG_SYSTEM_RESET', 'CONFIG_ROLES_READ'
    ]);
  }

  // 🔐 Auditoría de Planes restringida a un único email super-admin.
  // Espejo de la gate del backend / acciones admin en view-enrollment-v2.
  private readonly PAYMENT_PLAN_AUDIT_EMAIL = 'jdanigo@cloudsoft.site';
  get canViewPaymentPlanAudit(): boolean {
    const email = (this.currentUser?.email ?? '').toLowerCase();
    return email === this.PAYMENT_PLAN_AUDIT_EMAIL.toLowerCase();
  }

  get canViewEnrollmentAudit(): boolean {
    const email = (this.currentUser?.email ?? '').toLowerCase();
    return email === this.PAYMENT_PLAN_AUDIT_EMAIL.toLowerCase();
  }

  get canViewIntegrations(): boolean {
    return this.hasAnyPermission(['INTEGRATIONS_BITRIX_READ', 'INTEGRATIONS_WOMPI_READ']);
  }

  // Getters específicos de configuración
  get canViewConfigBudgets(): boolean {
    return this.hasPermission('CONFIG_BUDGETS_READ');
  }

  get canViewConfigBanks(): boolean {
    return this.hasPermission('CONFIG_BANKS_READ');
  }

  get canViewConfigPaymentMethods(): boolean {
    return this.hasPermission('CONFIG_PAYMENT_METHODS_READ');
  }

  get canViewConfigExchange(): boolean {
    return this.hasPermission('CONFIG_EXCHANGE_READ');
  }

  get canViewConfigFeeTypes(): boolean {
    return this.hasPermission('CONFIG_FEE_TYPES_READ');
  }

  get canViewConfigAudit(): boolean {
    return this.hasPermission('CONFIG_AUDIT_READ');
  }

  get canViewConfigEmailLogs(): boolean {
    return this.hasPermission('CONFIG_EMAILLOG_READ');
  }

  get canViewConfigSystemReset(): boolean {
    return this.hasPermission('CONFIG_SYSTEM_RESET');
  }

  get canViewIntegrationsBitrix(): boolean {
    return this.hasPermission('INTEGRATIONS_BITRIX_READ');
  }

  get canViewIntegrationsWompi(): boolean {
    return this.hasPermission('INTEGRATIONS_WOMPI_READ');
  }

  get canViewPaymentLinks(): boolean {
    return this.hasPermission('INTEGRATIONS_WOMPI_READ');
  }

  get canSendManualReminders(): boolean {
    return this.hasPermission('PAYMENT_REMINDERS_SEND_MANUAL') || this.isSuperAdmin;
  }

  // Método para cerrar sesión
  async logout(): Promise<void> {
    try {
      await this.authService.logout();
      this.toastService.showToast('success', 'Sesión cerrada correctamente');
      this.router.navigate(['/login']);
    } catch (error: any) {
      // Incluso si hay error, cerrar sesión localmente
      this.authService.logoutLocal();
      this.toastService.showToast('warning', 'Sesión cerrada localmente');
    }
  }
}
